import { computed, onMounted, onUnmounted, ref, watch, type Ref, type ComputedRef } from 'vue'
import { type EntryKey, useQuery, useQueryCache } from '@pinia/colada'
import {
  getPaymentAssociations,
  postPaymentAsync,
  postPaymentsUnassociated,
  type PaymentAssociationRow,
} from '@/api/lytAccountingClient'
import { useAuthStore } from '@/stores/authStore'
import { useTaskActivityStore } from '@/stores/taskActivityStore'

export type AssociationStatus = 'all' | 'associated' | 'unassociated' | 'syncing'

/** Stable Colada prefix for payment association list entries (no secrets). */
export const PAYMENT_ASSOCIATIONS_QUERY_KEY = 'payment-associations' as const

export interface UsePaymentsPage {
  // State refs
  associationRows: ComputedRef<PaymentAssociationRow[]>
  associationPage: Ref<number>
  associationPageSize: Ref<number>
  associationStatus: Ref<AssociationStatus>
  associationTotal: ComputedRef<number>
  associationLoading: ComputedRef<boolean>
  syncingRow: Ref<string | null>
  syncingAllFeedback: Ref<string>
  syncingAllLoading: Ref<boolean>
  error: ComputedRef<string>
  lastRefreshAt: Ref<Date | null>
  // Computed
  totalPages: ComputedRef<number>
  associatedCount: ComputedRef<number>
  unassociatedCount: ComputedRef<number>
  syncingCount: ComputedRef<number>
  // Handlers
  loadAssociations: () => Promise<void>
  syncSinglePayment: (payment: PaymentAssociationRow) => Promise<void>
  syncAllUnassociated: () => Promise<void>
  setAssociationStatus: (status: AssociationStatus) => Promise<void>
  setPageSize: (size: number) => Promise<void>
  prevPage: () => Promise<void>
  nextPage: () => Promise<void>
  formatDate: (dateStr: string | null) => string
}

/**
 * Build a stable, non-secret cache key for the payment associations list.
 * Includes entity, integration/store identity when known, pagination, and status filter.
 */
export function paymentAssociationsQueryKey(params: {
  integrationId: number | null
  storeId: string | null
  page: number
  pageSize: number
  status: AssociationStatus
}): EntryKey {
  return [
    PAYMENT_ASSOCIATIONS_QUERY_KEY,
    params.integrationId,
    params.storeId,
    params.page,
    params.pageSize,
    params.status,
  ]
}

/**
 * Composable for the PaymentsView page. Encapsulates the state-machine,
 * computed counts, and async handlers. Extracted from the SFC for unit
 * testing without a mount-based DOM environment.
 *
 * Association list reads go through Pinia Colada `useQuery`; writes stay on
 * the typed API client and invalidate the query cache after success.
 */
export function usePaymentsPage(): UsePaymentsPage {
  const taskActivity = useTaskActivityStore()
  const auth = useAuthStore()
  const queryCache = useQueryCache()
  let unsubscribeTaskActivity: (() => void) | null = null

  const associationPage = ref(1)
  const associationPageSize = ref(Number(localStorage.getItem('payments.pageSize') || 50))
  const associationStatus = ref<AssociationStatus>('all')
  const syncingRow = ref<string | null>(null)
  const syncingAllFeedback = ref('')
  const syncingAllLoading = ref(false)
  const actionError = ref('')
  const lastRefreshAt = ref<Date | null>(null)

  const integrationId = computed(() => auth.currentIntegration?.integrationId ?? null)
  const storeId = computed(
    () => auth.selectedStore?.storeId ?? (auth.storeID || null),
  )

  const {
    data,
    error: queryError,
    isLoading,
    refetch,
  } = useQuery({
    // Reactive key so filter/page/identity changes share distinct cache entries.
    key: () =>
      paymentAssociationsQueryKey({
        integrationId: integrationId.value,
        storeId: storeId.value,
        page: associationPage.value,
        pageSize: associationPageSize.value,
        status: associationStatus.value,
      }),
    query: async () =>
      getPaymentAssociations({
        page: associationPage.value,
        pageSize: associationPageSize.value,
        status: associationStatus.value === 'all' ? undefined : associationStatus.value,
      }),
  })

  const associationRows = computed(() => data.value?.payments ?? [])
  const associationTotal = computed(() => data.value?.total ?? 0)
  const associationLoading = computed(() => isLoading.value)
  const error = computed(() => {
    if (actionError.value) return actionError.value
    if (queryError.value) {
      return (queryError.value as Error)?.message || 'Failed to load payment associations'
    }
    return ''
  })

  const totalPages = computed(() => Math.ceil(associationTotal.value / associationPageSize.value) || 1)
  const associatedCount = computed(() => associationRows.value.filter(r => r.qboPaymentId).length)
  const unassociatedCount = computed(() => associationRows.value.filter(r => !r.qboPaymentId).length)
  const syncingCount = computed(() => associationRows.value.filter(r => r.runningTaskId).length)

  // Stamp last successful payload so the UI refresh label stays meaningful.
  watch(
    () => data.value,
    (next) => {
      if (next) lastRefreshAt.value = new Date()
    },
  )

  function formatDate(dateStr: string | null): string {
    return dateStr ? new Date(dateStr).toLocaleString() : '—'
  }

  async function loadAssociations() {
    syncingAllFeedback.value = ''
    actionError.value = ''
    await refetch()
  }

  async function syncSinglePayment(payment: PaymentAssociationRow) {
    if (payment.runningTaskId) return
    syncingRow.value = payment.lytsoftPaymentId
    actionError.value = ''
    try {
      await postPaymentAsync({ scope: 'single-sync', entityId: payment.lytsoftPaymentId })
      // Backend registration is not instantaneous; keep the 2s delay, then
      // targeted invalidation instead of a manual reload.
      setTimeout(() => {
        void queryCache.invalidateQueries({ key: [PAYMENT_ASSOCIATIONS_QUERY_KEY] })
      }, 2000)
    } catch (e) {
      actionError.value = (e as Error)?.message || 'Sync failed'
    } finally {
      syncingRow.value = null
    }
  }

  async function syncAllUnassociated() {
    syncingAllFeedback.value = ''
    syncingAllLoading.value = true
    actionError.value = ''
    try {
      const res = await postPaymentsUnassociated()
      if (res.taskId) {
        syncingAllFeedback.value = `Bulk sync started (${res.totalUnassociated} payments in batches, task ${res.taskId})`
        // Backend registration is not instantaneous; same delay as single-sync.
        setTimeout(() => {
          void queryCache.invalidateQueries({ key: [PAYMENT_ASSOCIATIONS_QUERY_KEY] })
        }, 2000)
      } else {
        syncingAllFeedback.value = 'No unassociated payments to sync'
      }
    } catch (e) {
      actionError.value = (e as Error)?.message || 'Bulk sync failed'
    } finally {
      syncingAllLoading.value = false
    }
  }

  async function setAssociationStatus(status: AssociationStatus) {
    associationStatus.value = status
    associationPage.value = 1
    // Reactive key change drives Colada; no explicit refetch.
  }

  async function setPageSize(size: number) {
    associationPageSize.value = size
    localStorage.setItem('payments.pageSize', String(size))
    associationPage.value = 1
    // Reactive key change drives Colada; no explicit refetch.
  }

  async function prevPage() {
    if (associationPage.value > 1) {
      associationPage.value--
      // Reactive key change drives Colada; no explicit refetch.
    }
  }

  async function nextPage() {
    if (associationPage.value < totalPages.value) {
      associationPage.value++
      // Reactive key change drives Colada; no explicit refetch.
    }
  }

  onMounted(() => {
    unsubscribeTaskActivity = taskActivity.subscribeActivity({
      includeRecent: true,
      includeActive: true,
      entityTypes: ['PAYMENT'],
      recentLimit: 50,
    })
    // useQuery already fetches on setup via the reactive key; loadAssociations
    // is reserved for explicit forced refreshes (e.g. post-link dialog close).
  })

  onUnmounted(() => {
    unsubscribeTaskActivity?.()
    unsubscribeTaskActivity = null
  })

  return {
    associationRows,
    associationPage,
    associationPageSize,
    associationStatus,
    associationTotal,
    associationLoading,
    syncingRow,
    syncingAllFeedback,
    syncingAllLoading,
    error,
    lastRefreshAt,
    totalPages,
    associatedCount,
    unassociatedCount,
    syncingCount,
    loadAssociations,
    syncSinglePayment,
    syncAllUnassociated,
    setAssociationStatus,
    setPageSize,
    prevPage,
    nextPage,
    formatDate,
  }
}
